import { lazy, Suspense, useCallback, useEffect, useState, type ComponentType } from 'react'
import { ConfigProvider } from 'antd'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { AuthScreen } from '@/components/auth-screen'
import { StartupScreen } from '@/components/startup-screen'
import { resolveStartupPhase } from '@/components/startup-screen/state'
import { APP_ROUTES } from '@/constants/routes'
import { AppAuthProvider, useAppAuth } from '@/context/AppAuthContext'
import { useBrandingLoad } from '@/hooks/chat/useBranding'
import type { CoreEntryPageProps } from '@/pages/core-entry-page'
import { XIAOCAI_CHAT_ANTD_THEME } from '@/theme/chatTheme'

type CoreEntryComponent = ComponentType<CoreEntryPageProps>
const InstanceAdministrationPage = lazy(() => import('@/pages/instance-administration-page'))
type CoreModuleLoadState = 'loading' | 'ready' | 'error'

function AppRoutes() {
  const {
    authError,
    authParams,
    authStage,
    authenticate,
    hasAccessToken,
    mockAuthEnabled,
    mockUsers,
    selectedMockUser,
    setSelectedMockUserId,
  } = useAppAuth()
  const brandingLoad = useBrandingLoad()
  const [CoreEntryPage, setCoreEntryPage] = useState<CoreEntryComponent | null>(null)
  const [coreModuleLoadState, setCoreModuleLoadState] = useState<CoreModuleLoadState>('loading')
  const [corePainted, setCorePainted] = useState(false)

  /** Start the heavy FLARE entry download in parallel with authentication and app-profile loading. */
  const loadCoreModule = useCallback(async () => {
    setCoreModuleLoadState('loading')
    try {
      const module = await import('@/pages/core-entry-page')
      setCoreEntryPage(() => module.default)
      setCoreModuleLoadState('ready')
    } catch {
      setCoreEntryPage(null)
      setCoreModuleLoadState('error')
    }
  }, [])

  useEffect(() => {
    void loadCoreModule()
  }, [loadCoreModule])

  const handleSubmitMock = useCallback((userId: string) => {
    void authenticate('mock', userId)
  }, [authenticate])
  const handleRetry = useCallback(() => {
    void authenticate()
  }, [authenticate])
  const handleCoreReady = useCallback(() => {
    setCorePainted(true)
  }, [])
  const handleStartupRetry = useCallback(() => {
    if (authStage === 'error') {
      void authenticate()
    }
    if (brandingLoad.loadState === 'error') {
      brandingLoad.reload()
    }
    if (coreModuleLoadState === 'error') {
      void loadCoreModule()
    }
  }, [authStage, authenticate, brandingLoad, coreModuleLoadState, loadCoreModule])

  const startupPhase = resolveStartupPhase({
    authStage,
    brandingLoadState: brandingLoad.loadState,
    coreModuleLoadState,
    corePainted,
    hasAccessToken,
  })

  const startupScreen = (
    <StartupScreen phase={startupPhase} errorMessage={authError || brandingLoad.error} onRetry={handleStartupRetry} />
  )

  if (!hasAccessToken) {
    return (
      <>
        {startupScreen}
        {mockAuthEnabled && authParams.mode === 'select' ? (
          <AuthScreen
            authError={authError}
            authMode={authParams.mode}
            authStage={authStage}
            mockAuthEnabled={mockAuthEnabled}
            mockUsers={mockUsers}
            onRetry={handleRetry}
            onSelectMockUser={setSelectedMockUserId}
            onSubmitMock={handleSubmitMock}
            selectedMockUser={selectedMockUser}
          />
        ) : null}
      </>
    )
  }

  return (
    <>
      {startupScreen}
      {CoreEntryPage && brandingLoad.loadState === 'ready' ? (
        <Routes>
          <Route
            path={APP_ROUTES.chat}
            element={<CoreEntryPage branding={brandingLoad.value} onReady={handleCoreReady} />}
          />
          <Route
            path="*"
            element={<CoreEntryPage branding={brandingLoad.value} onReady={handleCoreReady} />}
          />
        </Routes>
      ) : null}
    </>
  )
}

function App() {
  return (
    <ConfigProvider theme={XIAOCAI_CHAT_ANTD_THEME}>
      <BrowserRouter>
        <Routes>
          <Route path={APP_ROUTES.instanceAdministration} element={<Suspense fallback={null}><InstanceAdministrationPage /></Suspense>} />
          <Route path="*" element={<AppAuthProvider><AppRoutes /></AppAuthProvider>} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  )
}

export default App
